<template lang="pug">
  #portfolio.body: .body-inner
    //- Proportion block -------------------------------------------------------
    .proportion-block
      .col.no-select
        h4.title(v-text="text.proportion[0]")
        .value.text-left(v-text="proportionShares")
      .col.no-select
        h4.text-right.title(v-text="text.proportion[1]")
        .value.text-right(v-text="proportionBonds")
    //- Donut block ------------------------------------------------------------
    .donut-block
      .donut(ref="donutElement")
      .summary.no-select
        p.value {{ bondsCount + sharesCount }}
        p.text(v-text="summaryText")
    //- Tabs block -------------------------------------------------------------
    .tabs-block
      .tabs-header.no-select
        ul.tabs-btns
          li(:class="tabFirstIsActive"): button.tab-btn(type="button" @click="tabSwitch('first')")
            i.icon: UiIcon(icon="ui-icon-portfolio")
          li(:class="tabSecondIsActive"): button.tab-btn(type="button" @click="tabSwitch('second')")
            i.icon: UiIcon(icon="ui-icon-chart")
        img(width="1" height="1" :src="getTracking" v-if="getTracking")
        .tabs-additional
          .help: UiTooltip(:content="tooltips.structure" theme="light")
      .tabs-body
        .tab(v-show="tabs.first")
          //- Portfolio slider -------------------------------------------------
          UiSlider(class="portfolio-slider" :options="portfolioSlider" ref="portfolioSlider" @after-change="sliderChanged")
            button.prev-arrow(@click="sliderGoPrev"): UiIcon(icon="ui-slider-arrow-round")
            button.next-arrow(@click="sliderGoNext"): UiIcon(icon="ui-slider-arrow-round")
            PortfolioStructureSlide(
              v-for="(slide, index) in sliderData"
              :key="index"
              v-bind="slide"
            )
        .tab(v-show="tabs.second")
          //- Portfolio info ---------------------------------------------------
          .portfolio-info.no-select
            .info-row
              h4.title(v-text="'Инвестировано в акции:'")
              p.value {{ investedInShares|addCurSym }}
            .info-row
              h4.title(v-text="'Инвестировано в облигации:'")
              p.value {{ investedInBonds|addCurSym }}
            .info-row
              h4.title(v-text="'Свободный денежный остаток:'")
              p.value {{ freeCashBalance|addCurSym }}
</template>

<script>
import 'amcharts3' // Charts lib
import 'amcharts3/amcharts/pie' // Charts pie type
import PortfolioStructureSlide from '@/views/steps/PortfolioStructureSlide'
import UiSlider from '@/components/UiSlider'
import roundToOne from '@/utils/roundToOne'
import tdSplitter from '@/utils/thousandSplitter'
import { _cfg } from '@/config'
import { mapGetters, mapMutations, mapActions } from 'vuex'

export default {
  name: 'Portfolio',
  // Components ----------------------------------------------------------------
  components: {
    UiSlider,
    PortfolioStructureSlide
  },
  // Created ------------------------------------------------------------ [Hook]
  created () {
    this.SET_CURRENT_VIEW({ vm: this })
  },
  // Before destroy ----------------------------------------------------- [Hook]
  beforeDestroy () {
    // eslint-disable-next-line no-undef
    AmCharts.charts[0].clear()
  },
  // Data ----------------------------------------------------------------------
  data () {
    return {
      // Tooltips
      tooltips: _cfg.text.tooltips,
      // Component text
      text: _cfg.text.portfolioStructure,
      // Tabs
      tabs: {
        first: true,
        second: false
      },
      // Slider data
      sliderData: [],
      // Portfolio slider
      portfolioSlider: {
        rows: 0,
        arrows: false,
        slide: '.pf-slide',
        dots: true
      },
      freeCashBalance: 0,
      investedInBonds: 0,
      investedInShares: 0,
      bondsCount: 0,
      sharesCount: 0,
      slideByDonutRealClick: false,
      calcBondsProp: 0
    }
  },
  // Computed ------------------------------------------------------------------
  computed: {
    // Vuex map getters of the User SM
    ...mapGetters('moduleUser', [
      'getPortfolioData',
      'getTracking'
    ]),

    // Proportion of shares
    proportionShares () {
      let calcFormula = 100 - this.calcBondsProp
      calcFormula = roundToOne(calcFormula)
      return calcFormula + '%'
    },

    // Proportion of bonds
    proportionBonds () {
      let calcFormula = this.investedInBonds / (this.investedInBonds + this.investedInShares + this.freeCashBalance)
      calcFormula = roundToOne(calcFormula * 100)
      if (this.sharesCount < 1) {
        calcFormula = 100
      }
      if (this.bondsCount < 1) {
        calcFormula = 0
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.calcBondsProp = calcFormula
      return calcFormula + '%'
    },

    // First tab is active?
    tabFirstIsActive () {
      return {
        active: this.tabs.first
      }
    },

    // First tab is active?
    tabSecondIsActive () {
      return {
        active: this.tabs.second
      }
    },

    // Summary text
    summaryText () {
      return this.declOfNum(this.bondsCount + this.sharesCount, this.text.titles)
    }
  },
  // Watch ---------------------------------------------------------------------
  watch: {
    // Update slider
    'sliderData' (v) {
      this.$nextTick(() => {
        this.$refs.portfolioSlider.refresh()
      })
    }
  },
  // Filters -------------------------------------------------------------------
  filters: {
    // Split value by thousand
    tdSplit: function (value) { // Value format splitter
      return tdSplitter(value)
    },

    // Add currency symbol
    addCurSym (value) {
      return value + ' ' + _cfg.common.currency
    }
  },
  // Mounted -------------------------------------------------------------------
  mounted () {
    this.parseTickersData() // Parse
  },
  // Methods -------------------------------------------------------------------
  methods: {
    // Vuex map mutations of the Wizard SM
    ...mapMutations('moduleWizard', [
      'SET_CURRENT_VIEW',
      'SET_NAV_FIELD'
    ]),
    // Vuex map actions of the Security SM
    ...mapActions('moduleSecurity', [
      'sendMessage'
    ]),
    // Parse data logic
    parseTickersData () {
      const tickers = this.getPortfolioData.tickers
      this.freeCashBalance = tickers.money
      // Get invested in bonds
      this.investedInBonds = tickers.money_in_bonds
      // Get invested in shares
      this.investedInShares = tickers.money_in_tickers
      // Create array of all securities (bonds & shares)
      const securities = [] // Empty array
      // Iterate tickers if they are exist
      if (tickers.tickers && tickers.tickers.length > 0) {
        this.sharesCount = tickers.tickers.length
        for (let t = 0; t < tickers.tickers.length; t++) {
          securities.push({
            name: tickers.tickers[t].name,
            money: tickers.tickers[t].money,
            price: tickers.tickers[t].price,
            qty: tickers.tickers[t].qty,
            color: '#e30613', // demo color
            type: tickers.tickers[t].priv
              ? 'Акция привилегированная'
              : 'Акция обыкновенная'
          })
        }
      } else {
        this.sharesCount = 0
      }
      // Iterate bonds if they are exist
      if (tickers.bonds && tickers.bonds.length > 0) {
        this.bondsCount = tickers.bonds.length
        for (let b = 0; b < tickers.bonds.length; b++) { // Let it be, let it be! ... :)
          securities.push({
            name: tickers.bonds[b].name,
            money: tickers.bonds[b].money,
            price: tickers.bonds[b].price,
            qty: tickers.bonds[b].qty,
            color: '#e30613', // demo color
            type: 'Облигация'
          })
        }
      } else {
        this.bondsCount = 0
      }
      this.chartDonutInit(securities)
    },
    // Donut
    chartDonutInit (securities) {
      let sum = 0
      for (const x in securities) {
        sum += securities[x].money
      }
      securities.push({
        money: sum,
        name: 'empty',
        alpha: 0
      })
      // eslint-disable-next-line no-undef
      AmCharts.makeChart(this.$refs.donutElement, {
        type: 'pie',
        titleField: 'name',
        valueField: 'money',
        dataProvider: securities, // Empty on init
        startAngle: 0,
        pieY: '100%',
        radius: '76%',
        innerRadius: '66%',
        startDuration: 0,
        pullOutEffect: 'easeOutSine',
        pullOutDuration: 0.1,
        pullOutOnlyOne: true,
        hoverAlpha: 0.8,
        addClassNames: true,
        labelsEnabled: false,
        hideCredits: true,
        outlineThickness: 0,
        colors: _cfg.common.colors,
        balloon: {
          enabled: false
        },
        listeners: [
          {
            event: 'dataUpdated',
            method: e => {
              for (let i = 0; i < e.chart.chartData.length - 1; i++) {
                securities[i].color = e.chart.chartData[i].color
              }
              securities.pop()
              this.sliderData = securities
            }
          },
          {
            event: 'clickSlice',
            method: e => {
              if (e.dataItem.pulled) {
                if (e.event) {
                  this.slideByDonutRealClick = true
                  this.$refs.portfolioSlider.goTo(e.dataItem.index, true)
                }
              }
            }
          }
        ]
      })
    },
    // Slider changed
    sliderChanged (e, s, c) {
      // eslint-disable-next-line no-undef
      if (!this.slideByDonutRealClick) AmCharts.charts[0].clickSlice(c)
      this.slideByDonutRealClick = false
    },
    // Tab switch
    tabSwitch (tab) {
      if (tab === 'first') {
        this.tabs.first = true
        this.tabs.second = false
        this.$refs.portfolioSlider.refresh()
        // eslint-disable-next-line no-undef
        const index = AmCharts.charts[0].chartData.map(v => v.pulled).indexOf(true)
        // eslint-disable-next-line no-undef
        AmCharts.charts[0].clickSlice(index)
      } else {
        this.tabs.first = false
        this.tabs.second = true
      }
    },
    // Declension of num
    declOfNum (n, titles) {
      return titles[(n % 10 === 1 && n % 100 !== 11) ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2]
    },
    // Slider go previous
    sliderGoPrev () {
      this.$refs.portfolioSlider.prev()
    },
    // Slider go next
    sliderGoNext () {
      this.$refs.portfolioSlider.next()
    },
    // Self back validator
    async backSelf () {
      //
    },
    // Self next validator
    async nextSelf () {
      this.sendMessage({ e: 'PortfolioComplete' })
      return false
    }
  }
}
</script>
